import React, { useEffect, useRef, useState } from "react";
import { getPrivateFileObjectUrl } from "../../services/apiService";
import {
  formatMessageTime,
  groupMessagesByDate,
  leadDisplayName,
  leadInitials,
} from "./utils";

function EmptyState() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6 bg-slate-50/60">
      <div className="relative w-28 h-24 mb-4">
        <div className="absolute left-2 top-2 w-16 h-12 rounded-2xl bg-blue-100 border border-blue-200" />
        <div className="absolute right-0 top-0 w-16 h-12 rounded-2xl bg-white border border-slate-200 shadow-sm" />
        <div className="absolute left-8 bottom-0 w-16 h-12 rounded-2xl bg-blue-50 border border-blue-100" />
      </div>
      <h2 className="text-lg font-semibold text-slate-800">Start chatting</h2>
      <p className="text-sm text-slate-500 mt-1">Click on a conversation to start</p>
    </div>
  );
}

function AttachmentChip({ metadata }) {
  const name =
    metadata?.fileName
    || metadata?.attachmentName
    || metadata?.filename
    || null;
  if (!name) return null;
  return (
    <div className="mt-2 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700">
      <span className="text-red-500 font-bold">PDF</span>
      <span className="truncate max-w-[220px]">{name}</span>
    </div>
  );
}

function VoiceNotePlayer({ fileId, durationMs }) {
  const [src, setSrc] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(Boolean(fileId));

  useEffect(() => {
    let cancelled = false;
    let objectUrl = "";
    if (!fileId) {
      setLoading(false);
      setError("Missing voice file");
      return undefined;
    }
    setLoading(true);
    setError("");
    getPrivateFileObjectUrl(fileId)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setSrc(url);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load voice note");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileId]);

  if (loading) {
    return <div className="mt-2 text-xs text-slate-400">Loading voice note…</div>;
  }
  if (error || !src) {
    return <div className="mt-2 text-xs text-slate-400">{error || "Voice note unavailable"}</div>;
  }
  return (
    <div className="mt-2 space-y-1">
      <audio controls src={src} className="w-full max-w-[280px]" preload="metadata" />
      {durationMs ? (
        <div className="text-[11px] text-slate-400">
          {Math.max(1, Math.round(Number(durationMs) / 1000))}s
        </div>
      ) : null}
    </div>
  );
}

function MessageBody({ msg }) {
  const isVoice = msg.metadata?.kind === "VOICE_NOTE";
  const voiceFileId = msg.metadata?.voiceFileId;
  if (isVoice && voiceFileId) {
    return (
      <>
        {msg.content && msg.content !== "[Voice note]" ? (
          <div className="mb-1">{msg.content}</div>
        ) : (
          <div className="text-xs text-slate-500 mb-1">Voice note</div>
        )}
        <VoiceNotePlayer
          fileId={voiceFileId}
          durationMs={msg.metadata?.voiceDurationMs}
        />
        {msg.metadata?.fallbackUsed ? (
          <div className="mt-1 text-[11px] text-amber-600">Sent as text fallback</div>
        ) : null}
      </>
    );
  }
  return (
    <>
      {msg.content || msg.subject || (
        <em className="text-slate-400">No content</em>
      )}
      <AttachmentChip metadata={msg.metadata} />
    </>
  );
}

export default function ChatPane({
  threadSummary,
  threadDetail,
  loading,
  replyContent,
  sending,
  isFavorite,
  onReplyChange,
  onSend,
  onToggleFavorite,
  onRefresh,
}) {
  const endRef = useRef(null);
  const messages = threadDetail?.messages || [];
  const groups = groupMessagesByDate(messages);
  const talkingFrom =
    threadDetail?.leadContext?.talkingFrom?.accountName
    || threadSummary?.linkedinAccount?.accountName;

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, threadSummary?.threadId]);

  if (!threadSummary) {
    return <EmptyState />;
  }

  const lead = threadSummary.lead;

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {lead?.profileImageUrl ? (
            <img
              src={lead.profileImageUrl}
              alt=""
              className="w-9 h-9 rounded-full object-cover"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-xs font-semibold">
              {leadInitials(lead)}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">
              {leadDisplayName(lead)}
            </div>
            {talkingFrom ? (
              <div className="text-xs text-slate-500 truncate">
                Talking from {talkingFrom}
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onRefresh}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 text-sm"
            title="Refresh"
          >
            ↻
          </button>
          <button
            type="button"
            onClick={onToggleFavorite}
            className={`p-2 rounded-lg hover:bg-slate-100 text-sm ${
              isFavorite ? "text-amber-500" : "text-slate-400"
            }`}
            title={isFavorite ? "Unfavorite" : "Favorite"}
          >
            {isFavorite ? "★" : "☆"}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5 bg-slate-50/40">
        {loading ? (
          <div className="text-sm text-slate-400">Loading thread…</div>
        ) : (
          groups.map((group) => (
            <div key={group.key}>
              <div className="flex justify-center mb-4">
                <span className="text-[11px] text-slate-400 bg-white border border-slate-100 px-3 py-1 rounded-full">
                  {group.label}
                </span>
              </div>
              <div className="space-y-3">
                {group.messages.map((msg) => {
                  const outbound = msg.direction === "OUTBOUND";
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${outbound ? "justify-end" : "justify-start"}`}
                    >
                      <div className={`max-w-[75%] ${outbound ? "items-end" : "items-start"} flex flex-col`}>
                        <div className="text-[11px] text-slate-400 mb-1 px-1">
                          {outbound
                            ? msg.linkedinAccountName || talkingFrom || "You"
                            : msg.leadName || leadDisplayName(lead)}
                          {" · "}
                          {formatMessageTime(msg.sentAt || msg.createdAt)}
                        </div>
                        <div
                          className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words ${
                            outbound
                              ? "bg-slate-100 text-slate-800 rounded-tr-md"
                              : "bg-white border border-slate-200 text-slate-800 rounded-tl-md"
                          }`}
                        >
                          <MessageBody msg={msg} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>

      <div className="border-t border-slate-200 p-3 bg-white">
        <div className="rounded-xl border border-slate-200 focus-within:border-blue-400 transition-colors">
          <textarea
            value={replyContent}
            onChange={(e) => onReplyChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
            rows={3}
            placeholder="Message"
            className="w-full resize-none rounded-xl px-3 py-2.5 text-sm outline-none bg-transparent"
          />
          <div className="flex items-center justify-end gap-2 px-2 pb-2">
            <button
              type="button"
              disabled={sending || !replyContent.trim()}
              onClick={onSend}
              className="px-4 py-1.5 rounded-lg bg-blue-500 text-white text-sm font-medium disabled:opacity-40 hover:bg-blue-600"
            >
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
